import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  TextField,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const ChattitudeModal = ({ open, onClose, productName = 'Chattitude' }) => {
  const [form, setForm] = useState({ email: '', postUrl: '', accessToken: '' });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);

  const authHeader = () => {
    const jwtToken = Cookies.getJwtToken();
    if (jwtToken) return { Authorization: `Bearer ${jwtToken}` };
    // Fallback: backend supports direct tenant-id in Authorization for testing
    const tenantId = Cookies.getTenantId();
    return tenantId ? { Authorization: String(tenantId) } : {};
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/integrations/chattitude`, { headers: authHeader() });
        setForm({
          email: res.data?.email || '',
          postUrl: res.data?.postUrl || '',
          accessToken: res.data?.accessToken || '',
        });
      } catch (e) {
        setForm({ email: '', postUrl: '', accessToken: '' });
        setError(`Failed to fetch ${productName} settings.`);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchSettings();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/integrations/chattitude`, form, { headers: authHeader() });
      setSnackbar({ open: true, message: 'Settings saved successfully!', severity: 'success' });
      setTimeout(() => onClose && onClose(), 1200);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save settings.', severity: 'error' });
      setError(`Failed to save ${productName} settings.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ zIndex: 99999 }}
      PaperProps={{ style: { minWidth: 1200, minHeight: 800, zIndex: 99999 } }}
    >
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        {productName} Settings
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5', zIndex: 21000 }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Email (optional)"
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Post Url (optional)"
              name="postUrl"
              value={form.postUrl}
              onChange={handleChange}
              fullWidth
              helperText={`Want to receive the data from ${productName} in a webhook? Enter the URL here.`}
            />
            <TextField
              label="Access Token (optional)"
              name="accessToken"
              value={form.accessToken}
              onChange={handleChange}
              fullWidth
              helperText="If Webhook requires an Access Token, enter it here."
            />

<Box sx={{ mt: 3, mb: 2, p: 2, background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{`How to use the ${productName} tool`}</Typography>
                {`${productName} helps you receive leads and user details when your agent needs to collect user input.`}
                 
                <ol style={{ paddingLeft: 18, marginBottom: 8 }}>
                  <li>Email - To receive the leads on your email.</li>
                  <li>Post URL - To receive the post on your webhook.</li>
                  <li>Access Token - If Webhook requires an Access Token, enter it here.</li>
                </ol>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {`After connecting, ${productName} will securely store your credentials`}
                </Typography>
              </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 2, px: 3 }}>
        <Button onClick={handleSave} variant="outlined" color="primary" disabled={loading} sx={{ minWidth: 180, py: 1.2 }}>
          SAVE &amp; Close
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ChattitudeModal;

